import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Icon } from '@iconify/react'
import ReactQuill from 'react-quill'
import 'react-quill/dist/quill.snow.css'

import { Avatar, Drawer, Spin, Tag, Tooltip } from 'antd'

import { candidateAddNote, candidateNotesList } from '../store/candidate.slice'
import { CANDIDATE_STATUS } from './candidate-status-tab-list'
import { uploadResume, uploadResumeS3Url } from '../service/candidate.service'
import AttachmentPreview from '../../../components/attachmentPreview/attachmentPreview'
import DeletePopUp from '../../../components/deletePopUp/deletePopUp'
import { camelCaseToWordConverter } from '../../../utils/commonUtils'
import { getTalentAccess } from '../../../helpers/reportsPermission'
import styles from './candidate-note-drawer.module.css'

const initialState = {
    addCommentLoader: false,
    comment: '',
    deleteModal: false,
    deleteButtonLoading: false,
}

const NoteDrawer = ({ visible, onClose, candidate }) => {
    const [attachments, setAttachments] = useState([])
    const [noteInput, setNoteInput] = useState('')
    const [state, setState] = useState(initialState)
    const [isSaving, setIsSaving] = useState(false)
    const fileInputRef = useRef(null)
    const containerRef = useRef(null)
    const convertToUserStatus = 'convertToUser'

    const dispatch = useDispatch()
    const { userProfile } = useSelector((s) => s.loginSlice)
    const { candidateNotes, isLoading } = useSelector((s) => s.candidateSlice)
    const talentAccess = useMemo(
        () => getTalentAccess(userProfile),
        [userProfile]
    )

    const status = CANDIDATE_STATUS.find((s) => s?.value === candidate?.status)
    const noteCount = candidateNotes?.length || 0
    const canAddNote =
        talentAccess.canEdit && candidate?.status !== convertToUserStatus
    const canDeleteNote =
        talentAccess.canDelete && candidate?.status !== convertToUserStatus

    const getStatusExplanation = (statusValue) => {
        const match = CANDIDATE_STATUS.find((s) => s.label === statusValue)
        return match?.tooltip
    }

    const commentStatus = (statusValue) => {
        const match = CANDIDATE_STATUS.find((s) => s?.value === statusValue)
        return match?.color
    }

    const handleGetCandidateNotes = (candidateId) => {
        try {
            dispatch(candidateNotesList({ candidateId }))
        } catch (error) {
            console.error('Error fetching notes:', error)
        }
    }

    const handleAddNote = async (
        candidateId,
        uploadedAttachments = null,
        fieldName = 'add'
    ) => {
        if (!canAddNote) return
        if (!noteInput.trim()) return

        try {
            const payload = {
                candidateComment: {
                    content: noteInput,
                    createdDate: Date.now(),
                    status: candidate.status,
                    createdBy: userProfile._id,
                    attachments: uploadedAttachments,
                },
                fieldName,
            }

            const { data } = await candidateAddNote(candidateId, payload)

            if (data.success) {
                setNoteInput('')
                handleGetCandidateNotes(candidateId)
                setAttachments([])
            }
        } catch (error) {
            console.error('Error adding note:', error)
        }
    }

    const handleSaveNoteWithAttachments = async (
        candidateId,
        fieldName = 'add'
    ) => {
        setIsSaving(true)
        try {
            let uploadedLinks = null

            if (attachments.length) {
                const payload = {
                    data: attachments.map((f) => ({
                        filename: f.name,
                        contentType: f.type,
                    })),
                }

                const { data } = await uploadResume(payload)

                if (data.success) {
                    uploadedLinks = []

                    for (const file of attachments) {
                        const match = data.data.find(
                            (item) => item.filename === file.name
                        )
                        if (!match) {
                            console.error(
                                `No upload URL for file: ${file.name}`
                            )
                            continue
                        }

                        const response = await uploadResumeS3Url(
                            file,
                            match.uploadUrl,
                            match.publicLink,
                            match.name
                        )
                        if (response.ok) {
                            uploadedLinks.push({
                                title: file.name,
                                displayName: file.name,
                                category: 'document',
                                file: {
                                    fileName: file.name,
                                    fileLink: match.publicLink,
                                    fileType: file.type,
                                    uploadDate: Date.now(),
                                },
                            })
                        } else {
                            console.error(
                                `Failed to upload ${file.name}:`,
                                await response.text()
                            )
                        }
                    }

                    if (uploadedLinks.length === 0) uploadedLinks = null
                }
            }

            await handleAddNote(candidateId, uploadedLinks, fieldName)
        } catch (error) {
            console.error('Error saving note with attachments:', error)
        } finally {
            setIsSaving(false)
        }
    }

    const handleRemoveNotes = async (candidateId, fieldName = 'remove') => {
        if (!canDeleteNote) return
        try {
            const payload = {
                candidateCommentId: state?.comment?._id,
                fieldName,
            }

            const { data } = await candidateAddNote(candidateId, payload)
            if (data.success) {
                handleGetCandidateNotes(candidateId)
                setState((prevState) => ({
                    ...prevState,
                    addCommentLoader: false,
                    deleteButtonLoading: false,
                    deleteModal: false,
                }))
            }
        } catch (error) {
            console.log('🚀 ~ handleRemoveNotes ~ error:', error)
        }
    }

    const isMeaningfulContent = (html) => {
        if (!html) return false
        const temp = document.createElement('div')
        temp.innerHTML = html
        return temp.textContent.trim().length > 0
    }

    const groupNotesByDate = (notes) =>
        notes.reduce((acc, note) => {
            const dayKey = new Date(note.createdDate).toDateString()
            if (!acc[dayKey]) acc[dayKey] = []
            acc[dayKey].push(note)
            return acc
        }, {})

    const formatDateForHeader = (dateString) => {
        const today = new Date()
        const inputDate = new Date(dateString)
        const yesterday = new Date()
        yesterday.setDate(today.getDate() - 1)

        if (inputDate.toDateString() === today.toDateString()) return 'Today'
        if (inputDate.toDateString() === yesterday.toDateString())
            return 'Yesterday'

        return inputDate.toLocaleDateString('en-IN', {
            weekday: 'short',
            month: 'short',
            day: 'numeric',
            year: 'numeric',
        })
    }

    const groupedNotes = groupNotesByDate(candidateNotes || [])

    const canSubmit =
        Boolean(noteInput && noteInput.replace(/<[^>]*>/g, '').trim()) &&
        !isSaving

    const handleClose = () => {
        onClose(false)
        setNoteInput('')
        setAttachments([])
    }

    const handleFileChange = (event) => {
        const files = Array.from(event.target.files || [])
        if (files.length) {
            setAttachments((prev) => [...prev, ...files])
        }
        event.target.value = null
    }

    useEffect(() => {
        if (candidate?._id) {
            handleGetCandidateNotes(candidate._id)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [candidate])

    useEffect(() => {
        if (containerRef.current) {
            containerRef.current.scrollTop = containerRef.current.scrollHeight
        }
    }, [candidateNotes])

    return (
        <>
            <Drawer
                placement="right"
                open={visible}
                onClose={handleClose}
                width={480}
                className={styles.notesDrawer}
                rootClassName={styles.notesDrawerRoot}
                closable={false}
                destroyOnClose
                title={
                    <div className={styles.drawerHeader}>
                        <div className={styles.drawerHeaderLeft}>
                            <div className={styles.drawerHeaderIcon}>
                                <Icon
                                    icon="mdi:note-text-outline"
                                    width={20}
                                    height={20}
                                />
                            </div>
                            <div>
                                <div className={styles.drawerHeaderTitle}>
                                    View Notes
                                </div>
                                <div className={styles.drawerHeaderSubtitle}>
                                    {candidate?.fullName || 'Candidate'}
                                </div>
                            </div>
                        </div>
                        <div className={styles.drawerHeaderActions}>
                            {status ? (
                                <Tag
                                    color={status.color}
                                    className={styles.statusTag}
                                >
                                    {camelCaseToWordConverter(candidate?.status)}
                                </Tag>
                            ) : null}
                            <button
                                type="button"
                                className={styles.closeBtn}
                                onClick={handleClose}
                                aria-label="Close notes"
                            >
                                <Icon icon="mdi:close" width={18} height={18} />
                            </button>
                        </div>
                    </div>
                }
            >
                <div className={styles.layout}>
                    <section className={styles.notesPane}>
                        <div className={styles.notesPaneHeader}>
                            <p className={styles.notesPaneTitle}>Activity</p>
                            {!isLoading && noteCount > 0 ? (
                                <span className={styles.countPill}>
                                    {noteCount}{' '}
                                    {noteCount === 1 ? 'note' : 'notes'}
                                </span>
                            ) : null}
                        </div>

                        <div ref={containerRef} className={styles.notesScroll}>
                            {isLoading ? (
                                <div className={styles.centeredState}>
                                    <Spin />
                                </div>
                            ) : noteCount === 0 ? (
                                <div className={styles.centeredState}>
                                    <div className={styles.emptyIcon}>
                                        <Icon
                                            icon="mdi:note-off-outline"
                                            width={24}
                                            height={24}
                                        />
                                    </div>
                                    <p className={styles.emptyTitle}>
                                        No notes yet
                                    </p>
                                    <p className={styles.emptyText}>
                                        {canAddNote
                                            ? 'Add the first note using the form below.'
                                            : 'No notes found for this candidate.'}
                                    </p>
                                </div>
                            ) : (
                                Object.entries(groupedNotes).map(
                                    ([dateKey, notes]) => (
                                        <div
                                            key={dateKey}
                                            className={styles.dateGroup}
                                        >
                                            <div
                                                className={styles.dateDivider}
                                            >
                                                <span
                                                    className={styles.datePill}
                                                >
                                                    {formatDateForHeader(
                                                        dateKey
                                                    )}
                                                </span>
                                            </div>

                                            <div className={styles.notesList}>
                                                {notes.map((msg, index) => {
                                                    const time = new Date(
                                                        msg.createdDate
                                                    ).toLocaleTimeString(
                                                        'en-IN',
                                                        {
                                                            hour: '2-digit',
                                                            minute: '2-digit',
                                                        }
                                                    )
                                                    const userName =
                                                        msg?.createdByUser
                                                            ?.name || 'Unknown'
                                                    const noteStatus =
                                                        camelCaseToWordConverter(
                                                            msg?.status
                                                        )
                                                    const statusColor =
                                                        commentStatus(
                                                            msg?.status
                                                        )

                                                    return (
                                                        <article
                                                            key={
                                                                msg._id || index
                                                            }
                                                            className={
                                                                styles.noteCard
                                                            }
                                                        >
                                                            <div
                                                                className={
                                                                    styles.noteTop
                                                                }
                                                            >
                                                                <div
                                                                    className={
                                                                        styles.noteAuthorRow
                                                                    }
                                                                >
                                                                    <Avatar
                                                                        size={34}
                                                                        className={
                                                                            styles.noteAvatar
                                                                        }
                                                                    >
                                                                        {userName?.[0]?.toUpperCase()}
                                                                    </Avatar>
                                                                    <div
                                                                        className={
                                                                            styles.noteMeta
                                                                        }
                                                                    >
                                                                        <p
                                                                            className={
                                                                                styles.noteAuthor
                                                                            }
                                                                        >
                                                                            {
                                                                                userName
                                                                            }
                                                                        </p>
                                                                        <p
                                                                            className={
                                                                                styles.noteTime
                                                                            }
                                                                        >
                                                                            {
                                                                                time
                                                                            }
                                                                        </p>
                                                                    </div>
                                                                </div>

                                                                <div
                                                                    className={
                                                                        styles.noteActions
                                                                    }
                                                                >
                                                                    <Tooltip
                                                                        title={getStatusExplanation(
                                                                            noteStatus
                                                                        )}
                                                                    >
                                                                        <Tag
                                                                            color={
                                                                                statusColor
                                                                            }
                                                                            className={
                                                                                styles.noteStatusTag
                                                                            }
                                                                        >
                                                                            {
                                                                                noteStatus
                                                                            }
                                                                        </Tag>
                                                                    </Tooltip>
                                                                    {canDeleteNote ? (
                                                                        <Tooltip title="Delete">
                                                                            <button
                                                                                type="button"
                                                                                className={
                                                                                    styles.deleteBtn
                                                                                }
                                                                                onClick={() =>
                                                                                    setState(
                                                                                        (
                                                                                            prev
                                                                                        ) => ({
                                                                                            ...prev,
                                                                                            deleteModal: true,
                                                                                            comment:
                                                                                                msg,
                                                                                        })
                                                                                    )
                                                                                }
                                                                                aria-label="Delete note"
                                                                            >
                                                                                <Icon
                                                                                    icon="mdi:trash-can-outline"
                                                                                    width={
                                                                                        15
                                                                                    }
                                                                                    height={
                                                                                        15
                                                                                    }
                                                                                />
                                                                            </button>
                                                                        </Tooltip>
                                                                    ) : null}
                                                                </div>
                                                            </div>

                                                            {isMeaningfulContent(
                                                                msg.content
                                                            ) ? (
                                                                <div
                                                                    className={
                                                                        styles.noteContent
                                                                    }
                                                                    dangerouslySetInnerHTML={{
                                                                        __html: msg.content,
                                                                    }}
                                                                />
                                                            ) : null}

                                                            {msg?.attachments
                                                                ?.length >
                                                            0 ? (
                                                                <div
                                                                    className={
                                                                        styles.attachmentsWrap
                                                                    }
                                                                >
                                                                    {msg.attachments.map(
                                                                        (
                                                                            fileObj,
                                                                            i
                                                                        ) => (
                                                                            <AttachmentPreview
                                                                                key={
                                                                                    fileObj._id ||
                                                                                    i
                                                                                }
                                                                                file={
                                                                                    fileObj
                                                                                }
                                                                            />
                                                                        )
                                                                    )}
                                                                </div>
                                                            ) : null}
                                                        </article>
                                                    )
                                                })}
                                            </div>
                                        </div>
                                    )
                                )
                            )}
                        </div>
                    </section>

                    {canAddNote ? (
                        <section className={styles.composerPane}>
                            <div className={styles.composerCard}>
                                <div>
                                    <p className={styles.composerLead}>
                                        Add a note
                                    </p>
                                    <p className={styles.composerHint}>
                                        Share feedback or next steps for this
                                        candidate.
                                    </p>
                                </div>

                                <ReactQuill
                                    theme="snow"
                                    className={styles.noteEditor}
                                    onChange={setNoteInput}
                                    value={noteInput}
                                    placeholder="Write a note…"
                                />

                                <div className={styles.composerToolbar}>
                                    <div className={styles.leftTools}>
                                        <input
                                            ref={fileInputRef}
                                            type="file"
                                            multiple
                                            style={{ display: 'none' }}
                                            onChange={handleFileChange}
                                        />
                                        <button
                                            type="button"
                                            className={styles.attachBtn}
                                            onClick={() =>
                                                fileInputRef.current?.click()
                                            }
                                        >
                                            <Icon
                                                icon="mdi:paperclip"
                                                width={16}
                                                height={16}
                                            />
                                            Attachment
                                        </button>
                                        {attachments.length > 0 ? (
                                            <div className={styles.fileChips}>
                                                {attachments.map((file) => (
                                                    <span
                                                        key={`${file.name}-${file.lastModified}`}
                                                        className={
                                                            styles.fileChip
                                                        }
                                                    >
                                                        <span
                                                            className={
                                                                styles.fileChipName
                                                            }
                                                        >
                                                            {file.name}
                                                        </span>
                                                        <button
                                                            type="button"
                                                            className={
                                                                styles.fileChipRemove
                                                            }
                                                            onClick={() =>
                                                                setAttachments(
                                                                    (prev) =>
                                                                        prev.filter(
                                                                            (
                                                                                item
                                                                            ) =>
                                                                                item !==
                                                                                file
                                                                        )
                                                                )
                                                            }
                                                            aria-label={`Remove ${file.name}`}
                                                        >
                                                            <Icon
                                                                icon="mdi:close"
                                                                width={14}
                                                                height={14}
                                                            />
                                                        </button>
                                                    </span>
                                                ))}
                                            </div>
                                        ) : null}
                                    </div>

                                    <button
                                        type="button"
                                        className={styles.submitBtn}
                                        disabled={!canSubmit}
                                        onClick={() =>
                                            handleSaveNoteWithAttachments(
                                                candidate._id
                                            )
                                        }
                                    >
                                        {isSaving ? (
                                            <>
                                                <Icon
                                                    icon="line-md:loading-loop"
                                                    width={16}
                                                    height={16}
                                                />
                                                Saving...
                                            </>
                                        ) : (
                                            'Add Note'
                                        )}
                                    </button>
                                </div>
                            </div>
                        </section>
                    ) : null}
                </div>
            </Drawer>

            <DeletePopUp
                previewDelete={state?.deleteModal}
                onDelete={() => {
                    setState((prevState) => ({
                        ...prevState,
                        deleteButtonLoading: true,
                    }))
                    handleRemoveNotes(candidate._id, 'remove')
                }}
                loadingButton={state?.deleteButtonLoading}
                onCancel={() => {
                    setState((prevState) => ({
                        ...prevState,
                        deleteButtonLoading: false,
                        deleteModal: false,
                    }))
                }}
                modalTitle="Delete Comment"
                pageKey="Comment"
            />
        </>
    )
}

export default NoteDrawer
